import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Grid,
  Chip,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import api from '../services/api';
import { Project } from '../types';
import { format, isSameDay, isBefore, startOfDay } from 'date-fns';

type DayEntry = {
  date: string; // ISO date string, e.g. '2025-12-09'
  hoursWorked: string;
  description: string;
  submitting: boolean;
  submitted: boolean;
  errors?: {
    hoursWorked?: string;
    description?: string;
  };
};

const ContractorTimeEntryForm: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState<string>('');
  const [dayEntries, setDayEntries] = useState<DayEntry[]>([]);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [maxDatesError, setMaxDatesError] = useState<string>('');
  const navigate = useNavigate();

  const fetchProjects = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get('/contractor/projects');
      setProjects(response.data);
      
      if (projectId && !response.data.find((p: Project) => p.id === projectId)) {
        setProjectId('');
      }
    } catch (error: any) {
      console.error('Failed to fetch projects:', error);
      if (!silent && error.response?.status !== 401) {
        alert(`Failed to load projects: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjects();

    // Auto-refresh projects every 15 seconds to sync assignment changes
    const interval = setInterval(() => {
      fetchProjects(true);
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchProjects]);

  const handleDateClick = (date: Date | null) => {
    if (!date) return;

    const today = startOfDay(new Date());
    const clickedDate = startOfDay(date);

    // Prevent future dates
    if (clickedDate > today) {
      return;
    }

    // Check if date is already selected
    const isSelected = selectedDates.some(d => isSameDay(d, clickedDate));
    
    if (isSelected) {
      // Remove date
      const newSelectedDates = selectedDates.filter(d => !isSameDay(d, clickedDate));
      setSelectedDates(newSelectedDates);
      
      // Remove corresponding day entry
      const dateStr = format(clickedDate, 'yyyy-MM-dd');
      setDayEntries(dayEntries.filter(e => e.date !== dateStr));
    } else {
      // Check if we've reached the limit
      if (selectedDates.length >= 7) {
        setMaxDatesError('You can select up to 7 days at a time.');
        setTimeout(() => setMaxDatesError(''), 3000);
        return;
      }

      // Add date
      const newSelectedDates = [...selectedDates, clickedDate].sort((a, b) => a.getTime() - b.getTime());
      setSelectedDates(newSelectedDates);

      // Add new day entry
      const dateStr = format(clickedDate, 'yyyy-MM-dd');
      const newEntry: DayEntry = {
        date: dateStr,
        hoursWorked: '',
        description: '',
        submitting: false,
        submitted: false,
      };
      setDayEntries([...dayEntries, newEntry].sort((a, b) => a.date.localeCompare(b.date)));
    }
  };

  const handleCancelDay = (date: string) => {
    const entryDate = new Date(date);
    setSelectedDates(selectedDates.filter(d => !isSameDay(d, entryDate)));
    setDayEntries(dayEntries.filter(e => e.date !== date));
  };

  const validateDayEntry = (entry: DayEntry): boolean => {
    const errors: { hoursWorked?: string; description?: string } = {};

    if (!entry.hoursWorked || parseFloat(entry.hoursWorked) <= 0) {
      errors.hoursWorked = 'Hours must be greater than 0';
    }

    if (parseFloat(entry.hoursWorked) > 24) {
      errors.hoursWorked = 'Hours cannot exceed 24 per day';
    }

    if (!entry.description.trim()) {
      errors.description = 'Description is required';
    }

    setDayEntries(dayEntries.map(e => 
      e.date === entry.date ? { ...e, errors } : e
    ));

    return Object.keys(errors).length === 0;
  };

  const handleSubmitDay = async (entry: DayEntry) => {
    if (!projectId) {
      alert('Please select a project first');
      return;
    }

    if (!validateDayEntry(entry)) {
      return;
    }

    // Mark as submitting
    setDayEntries(dayEntries.map(e => 
      e.date === entry.date ? { ...e, submitting: true } : e
    ));

    try {
      await api.post('/contractor/time-entries', {
        projectId,
        dateWorked: new Date(entry.date).toISOString(),
        hoursWorked: parseFloat(entry.hoursWorked),
        description: entry.description,
      });

      // Mark as submitted and remove from list
      setDayEntries(dayEntries.map(e => 
        e.date === entry.date ? { ...e, submitting: false, submitted: true } : e
      ));

      // Remove from selected dates and entries after a short delay
      setTimeout(() => {
        const entryDate = new Date(entry.date);
        setSelectedDates(selectedDates.filter(d => !isSameDay(d, entryDate)));
        setDayEntries(dayEntries.filter(e => e.date !== entry.date));
      }, 1500);

    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to save time entry';
      
      if (error.response?.data?.existingEntry) {
        alert('Time entry already exists for this project and date. Please edit the existing entry instead.');
      } else {
        alert(errorMessage);
      }

      // Mark as not submitting
      setDayEntries(dayEntries.map(e => 
        e.date === entry.date ? { ...e, submitting: false } : e
      ));
    }
  };

  const updateDayEntry = (date: string, field: 'hoursWorked' | 'description', value: string) => {
    setDayEntries(dayEntries.map(e => 
      e.date === date ? { ...e, [field]: value, errors: undefined } : e
    ));
  };

  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  // Custom day renderer to highlight selected dates
  const CustomDay = (props: PickersDayProps<Date>) => {
    const { day, ...other } = props;
    const isSelected = selectedDates.some(d => isSameDay(d, day));
    const isToday = isSameDay(day, new Date());
    const isFuture = isBefore(new Date(), startOfDay(day));

    return (
      <PickersDay
        {...other}
        day={day}
        onClick={() => !isFuture && handleDateClick(day)}
        sx={{
          ...(isSelected && {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }),
          ...(isToday && !isSelected && {
            border: '2px solid',
            borderColor: 'primary.main',
          }),
          ...(isFuture && {
            opacity: 0.3,
            cursor: 'not-allowed',
          }),
        }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Log New Time Entry
      </Typography>

      <Paper sx={{ p: 3, mt: 3, maxWidth: 1200 }}>
        <Box component="form">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth required>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectId}
                label="Project"
                onChange={(e) => setProjectId(e.target.value)}
                disabled={projects.length === 0}
              >
                {projects.length === 0 ? (
                  <MenuItem disabled value="">
                    No projects assigned. Please contact administrator.
                  </MenuItem>
                ) : (
                  projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {projects.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  You need to be assigned to at least one project to log time entries.
                </Typography>
              )}
            </FormControl>

            {maxDatesError && (
              <Alert severity="warning" onClose={() => setMaxDatesError('')}>
                {maxDatesError}
              </Alert>
            )}

            <Box>
              <Typography variant="h6" gutterBottom>
                Select Dates (up to 7 days)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click on dates in the calendar to select them. Selected dates will appear below for you to enter hours and descriptions.
              </Typography>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Paper sx={{ p: 2, display: 'inline-block' }}>
                  <DateCalendar
                    value={calendarDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setCalendarDate(newValue);
                      }
                    }}
                    maxDate={new Date()}
                    shouldDisableDate={(day) => isBefore(new Date(), startOfDay(day))}
                    slots={{
                      day: CustomDay,
                    }}
                  />
                </Paper>
              </LocalizationProvider>

              {selectedDates.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDates.length} of 7 days selected
                  </Typography>
                </Box>
              )}
            </Box>

            {dayEntries.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Time Entries ({dayEntries.filter(e => !e.submitted).length} remaining)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dayEntries.map((entry) => {
                    const entryDate = new Date(entry.date);
                    const isSubmitted = entry.submitted;

                    return (
                      <Card key={entry.date} variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {format(entryDate, 'EEEE, MMMM d, yyyy')}
                            </Typography>
                            {isSubmitted ? (
                              <Chip label="Submitted" color="success" size="small" />
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => handleCancelDay(entry.date)}
                                disabled={entry.submitting}
                              >
                                <CancelIcon />
                              </IconButton>
                            )}
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Hours Worked"
                                type="number"
                                value={entry.hoursWorked}
                                onChange={(e) => updateDayEntry(entry.date, 'hoursWorked', e.target.value)}
                                fullWidth
                                required
                                disabled={isSubmitted || entry.submitting}
                                error={!!entry.errors?.hoursWorked}
                                helperText={entry.errors?.hoursWorked || 'Enter hours worked (e.g., 2.5 for 2 hours 30 minutes)'}
                                inputProps={{ step: '0.25', min: '0', max: '24' }}
                              />
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                label="Description of Work"
                                value={entry.description}
                                onChange={(e) => updateDayEntry(entry.date, 'description', e.target.value)}
                                fullWidth
                                required
                                multiline
                                rows={2}
                                disabled={isSubmitted || entry.submitting}
                                error={!!entry.errors?.description}
                                helperText={entry.errors?.description || `${entry.description.length} characters`}
                                placeholder="Describe the work you performed..."
                              />
                            </Grid>
                          </Grid>

                          {!isSubmitted && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                startIcon={entry.submitting ? <CircularProgress size={16} /> : <SaveIcon />}
                                onClick={() => handleSubmitDay(entry)}
                                disabled={entry.submitting || !projectId}
                              >
                                {entry.submitting ? 'Submitting...' : 'Submit This Day'}
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}

            {dayEntries.length === 0 && (
              <Alert severity="info">
                Select dates from the calendar above to start logging time entries.
              </Alert>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/contractor/time-entries')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ContractorTimeEntryForm;
